import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  useNavigate,
  useLocation,
  matchPath,
  Navigate,
} from "react-router-dom";
import {
  getCompanyAgencyOnboardingPath,
  getDefaultRouteForRole,
  getUserRole,
  hasFullName,
  isPathAllowedForRole,
  isCandidateApplicationPath,
  isPendingUserRoleSelection,
  requiresCompanyAgencyOnboarding,
  ROLE_IDS,
} from "../constants/roleConfig";
import loginCodeStore from "../zustand/loginCodeStore";
import { getMailtokenFromSearch } from "../utils/urlUtils";
import instance from "../api/instance";
import {
  getResumeJsonForCurrentUser,
  validateInviteLink,
} from "../api/apiRoutes";
import { setJobseekerId } from "../redux/reducers/authReducer";
import RouteLoading from "../components/CommonComponents/RouteLoading";

function PrivateRoute({ element, excludedRoutes = [] }) {
  const dispatch = useDispatch();
  const authToken = useSelector((state) => state.auth.token);
  const role = useSelector((state) => getUserRole(state.auth?.user));
  const job_seeker_id = useSelector(
    (state) => state.auth.user?.job_seeker_id ?? state.auth?.jobseeker_id,
  );
  const profile_completed = useSelector(
    (state) => state?.auth?.user?.profile_completed,
  );
  const groups = useSelector((state) => state?.auth?.user?.groups);
  const company_id = useSelector((state) => state?.auth?.user?.company_id);
  const full_name = useSelector((state) => state?.auth?.user?.full_name);
  const location = useLocation();
  const navigate = useNavigate();

  const [redirected, setRedirected] = useState(false);
  const [validatingLink, setValidatingLink] = useState(false);

  const isExcludedRoute = excludedRoutes.some((excludedRoute) =>
    matchPath(excludedRoute, location.pathname),
  );

  useEffect(() => {
    if (!redirected && !isExcludedRoute) {
      if (!authToken) {
        const searchParams = new URLSearchParams(location.search);
        const isClientDashboard =
          location.pathname.startsWith("/client-dashboard");
        const isViewJobsResults = location.pathname.startsWith(
          "/view-jobs-data/results",
        );
        // Read email from raw query so "+" is not decoded as space (URLSearchParams.get does that)
        const emailFromQuery = (() => {
          const m = location.search.match(/[?&]email=([^&]*)/);
          return m ? decodeURIComponent(m[1]) : null;
        })();
        // Optional: email in first path segment after /client-dashboard/ (e.g. /client-dashboard/user%40example.com)
        const pathSegments = location.pathname
          .replace(/^\/client-dashboard\/?/, "")
          .split("/")
          .filter(Boolean);
        const emailFromPath =
          pathSegments[0] && pathSegments[0].includes("@")
            ? decodeURIComponent(pathSegments[0])
            : null;
        const email =
          (emailFromQuery && emailFromQuery.trim()) ||
          (emailFromPath && emailFromPath.trim()) ||
          null;

        const mailtoken = getMailtokenFromSearch(location.search);

        const isOnboardingPath = location.pathname.startsWith("/onboarding");
        const isInvitationSource =
          searchParams.get("source") === "invitation" ||
          searchParams.get("from") === "invitation";

        if (isOnboardingPath && isInvitationSource && email) {
          const proceedToVerification = () => {
            loginCodeStore.getState().setMailToken(mailtoken);
            const verificationSearch = new URLSearchParams();
            verificationSearch.set("email", email);
            verificationSearch.set("source", "invitation");
            if (mailtoken) {
              verificationSearch.set("mailtoken", mailtoken);
            }
            const verificationPath = `/new-login-with-verification-code?${verificationSearch.toString()}`;
            navigate(verificationPath, {
              state: {
                from: location,
                redirectTo: location.pathname + location.search,
              },
              replace: true,
            });
            setRedirected(true);
          };

          setValidatingLink(true);
          instance
            .post(validateInviteLink, {
              email: email,
              mail_token: mailtoken,
            })
            .then(() => {
              proceedToVerification();
            })
            .catch((error) => {
              const status = error?.response?.status;
              if (status === 400 || status === 403) {
                navigate("/link-expired", { replace: true });
                setRedirected(true);
              } else {
                proceedToVerification();
              }
            })
            .finally(() => {
              setValidatingLink(false);
            });
        } else if ((isClientDashboard || isViewJobsResults) && email) {
          const proceedToClientVerification = () => {
            const verificationSearch = new URLSearchParams();
            verificationSearch.set("email", email);
            if (
              searchParams.get("from") === "client" ||
              searchParams.get("source") === "client"
            ) {
              verificationSearch.set("from", "client");
            }
            const verificationPath = `/new-login-with-verification-code?${verificationSearch.toString()}`;
            navigate(verificationPath, {
              state: {
                from: location,
                redirectTo: location.pathname + location.search,
              },
              replace: true,
            });
            setRedirected(true);
          };

          // Validate invite link if mailtoken is present
          if (mailtoken) {
            setValidatingLink(true);
            instance
              .post(validateInviteLink, {
                email: email,
                mail_token: mailtoken,
              })
              .then(() => {
                proceedToClientVerification();
              })
              .catch((error) => {
                const status = error?.response?.status;
                if (status === 400 || status === 403) {
                  navigate("/link-expired", { replace: true });
                  setRedirected(true);
                } else {
                  proceedToClientVerification();
                }
              })
              .finally(() => {
                setValidatingLink(false);
              });
          } else {
            proceedToClientVerification();
          }
        } else {
          // Pass through client context: email links use source=client, login page expects from=client
          const isClientLink =
            searchParams.get("from") === "client" ||
            searchParams.get("source") === "client";
          loginCodeStore.getState().setMailToken(null);
          const loginPath = isClientLink ? "/login?from=client" : "/login";
          navigate(loginPath, { state: { from: location }, replace: true });
          setRedirected(true);
        }
      } else if (!isPathAllowedForRole(location.pathname, role, job_seeker_id)) {
        // Path belongs to another role (e.g. client on /dashboard → client-dashboard)
        const defaultRoute = getDefaultRouteForRole(role);
        if (defaultRoute) {
          navigate(defaultRoute, { replace: true });
          setRedirected(true);
        }
      } else if (role === 2 && !job_seeker_id) {
        const jobIdMatch = location.pathname.match(
          /\/(?:related-)?jobs\/([^/?#]+)/,
        );
        const jobId = jobIdMatch ? jobIdMatch[1] || jobIdMatch[2] : null;

        const redirectToOnboarding = () => {
          if (jobId) {
            // Preserve source location so callers can navigate back after onboarding
            navigate(`/jobs/related-jobs/${jobId}`, {
              replace: true,
              state: { from: location },
            });
          } else {
            navigate("/resume-builder", {
              state: {
                from: location,
              },
              replace: true,
            });
          }
          setRedirected(true);
        };

        // A jobseeker id is written into auth state exactly once - by the resume
        // upload - and nothing in the app can re-derive it. Every login/OTP path
        // writes whatever the login response held, which is null for a lead-form
        // candidate because they verify their email BEFORE uploading. So if that
        // single write is lost (a refresh mid-flow, a tab seeded from a
        // pre-upload snapshot), the client believes there is no resume while the
        // server knows there is - and every non-job route bounces here forever.
        //
        // get-resume-json/ takes no id: it resolves the caller’s own record and
        // 404s when there genuinely is none. So ask the server before
        // redirecting. 200 repairs the state and the user carries on; 404 falls
        // through to exactly the previous behaviour.
        instance
          .get(getResumeJsonForCurrentUser)
          .then((res) => {
            const recoveredId = res?.data?.data?.id;
            if (recoveredId != null) {
              dispatch(setJobseekerId(recoveredId));
              return;
            }
            redirectToOnboarding();
          })
          .catch(() => {
            // 404 (no jobseeker record) or any transport failure: keep the
            // original guard behaviour rather than letting the user through.
            redirectToOnboarding();
          });
      }
    }
  }, [
    authToken,
    role,
    job_seeker_id,
    navigate,
    location,
    redirected,
    isExcludedRoute,
    dispatch,
  ]);

  // Role 3 (User): block all private dashboards until /user-role is completed (CheckRoute
  // only wraps public pages — direct /dashboard URLs must be blocked here).
  // Exclude /user-role itself to avoid a redirect loop.
  // A candidate part-way through an application must not be asked to pick an
  // account type. A fresh account is API role 3 until update-role runs, and the
  // jobseeker id only appears once the resume is uploaded - so on these paths
  // isPendingUserRoleSelection is true for someone who is plainly a jobseeker
  // and simply has not uploaded yet. Sending them to /user-role abandons the
  // application (and undid the redirect resolvePostLoginRedirect just made).
  // Uploading sets the id, after which this guard stops matching on its own.

  if (
    authToken &&
    isPendingUserRoleSelection(role, job_seeker_id) &&
    !isCandidateApplicationPath(location.pathname) &&
    location.pathname !== "/user-role" &&
    !location.pathname.startsWith("/user-role/")
  ) {
    return <Navigate to="/user-role" replace />;
  }

  // Company/agency owners: block /dashboard until onboarding submit sets profile_completed
  // (unless they already have company_id from the API).
  if (
    authToken &&
    requiresCompanyAgencyOnboarding(
      role,
      profile_completed,
      groups,
      company_id,
    ) &&
    !location.pathname.startsWith("/onboarding") &&
    !location.pathname.startsWith("/try-haire/interview")
  ) {
    return <Navigate to={getCompanyAgencyOnboardingPath(role)} replace />;
  }

  // Invited recruiter/admin/agency with no full_name: must complete name-only onboarding
  // before accessing dashboard. This catches the case where company_id is already set
  // (linked to org) but the user never entered their name.
  if (
    authToken &&
    (role === ROLE_IDS.RECRUITER || role === ROLE_IDS.AGENCY) &&
    !hasFullName(full_name) &&
    !location.pathname.startsWith("/onboarding") &&
    !location.pathname.startsWith("/try-haire/interview")
  ) {
    return <Navigate to={getCompanyAgencyOnboardingPath(role)} replace />;
  }

  if (validatingLink) {
    return <RouteLoading />;
  }

  // Render the element only if conditions are met
  // job_seeker_id is passed so a role 3 candidate mid-application is judged against the
  // applicant route set (the same rule RoleBasedRoutes renders with), not the recruiter one.
  const pathAllowed = isPathAllowedForRole(
    location.pathname,
    role,
    job_seeker_id
  );
  const applicantOk = role !== 2 || job_seeker_id;
  if (isExcludedRoute || (authToken && pathAllowed && applicantOk)) {
    return element;
  }

  // Prevent any output until navigation is decided
  return null;
}

export default PrivateRoute;
